import { useStore, useStoreUI } from '@assets/model/Store';
import { useNavUtils } from '@assets/utils/NavUtils';
import { useSpringValue } from '@react-spring/web';
import assert from 'assert';
import { throttle } from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Draggable from 'react-draggable';
import { Split } from '../Split/Split';
import { Flex } from '../UI/Flex/Flex';
import * as style from './DraggableSplit.module.less';
import dragIcon from './dragIcon.svg';
import useMeasure from 'react-use-measure';

interface IDragSplit {
    children: React.ReactNode;
    spaceBetween?: number;
    onStartDrag?: () => void;
    onStopDrag?: () => void;
}

export const DraggableSplit = observer((props: IDragSplit) => {
    const spaceBetween = props.spaceBetween || 20;
    const children = React.Children.toArray(props.children);
    assert(children.length === 2, 'Must be 2 children');

    const store = useStore();
    const nav = useNavUtils();
    const uiStore = useStoreUI();

    const [highlightDrag, setHighlightDrag] = useState(false);

    const [contentSize, setContentSize] = useState(
        uiStore.currentSplitRatioDetailView
    );

    const setContentSizeThrottle = useCallback(
        throttle(size => {
            setContentSize(size);
        }, 25),
        []
    );
    const [dragPosX, setDragPosX] = useState(0);
    const [ref, bounds] = useMeasure();

    const winWidth = bounds.width;
    const repositionDragable = () => {
        setDragPosX(winWidth * (contentSize / 100));
    };

    const resizeRefCallback = useRef(() => {});
    resizeRefCallback.current = repositionDragable;

    const draggableSpring = useSpringValue(0, {
        onChange: val => {
            const ui = useStoreUI();
            setContentSize(val as any);
        },
        onRest: val => {
            resizeRefCallback.current();
        }
    });

    useEffect(() => {
        uiStore.currentSplitRatioDetailView = contentSize;
    }, [contentSize]);

    useEffect(() => {
        repositionDragable();
    }, [winWidth]);

    if (!store.query) {
        return null;
    }

    const size = [contentSize, 100 - contentSize];
    return (
        <>
            {winWidth > 0 && (
                <Draggable
                    axis="x"
                    handle=".handle"
                    bounds={{ left: 300, right: winWidth - 300 }}
                    positionOffset={{ x: -5, y: 50 }}
                    // position={{
                    //     x: dragPos,
                    //     y: 0
                    // }}
                    //position={{ x: dragPosX, y: 0 }}
                    position={{
                        x: dragPosX,
                        y: 0
                    }}
                    onDrag={(e, data) => {
                        //Maybe add callback later if becomes messy not really draggable responsability
                        var pdfs =
                            document.getElementsByClassName('pdf-viewer');
                        for (var i = 0; i < pdfs.length; i++) {
                            const pdf = pdfs[i] as HTMLElement;
                            pdf.style.pointerEvents = 'none';
                        }

                        var x = data.x;
                        var mouseX = data.lastX;
                        var normalized = data.x / winWidth;
                        // console.log(
                        //     'DRAGGING: ' + x + ' ' + mouseX + ' ' + winWidth
                        // );
                        const viewSize = normalized * 100;

                        setContentSizeThrottle(viewSize);
                    }}
                    onStop={(e, data) => {
                        //console.log('STOP DRAG: ', data.x);
                        var normalized = data.x / winWidth;
                        const viewSize = normalized * 100;
                        setContentSize(viewSize);
                        repositionDragable();

                        var pdfs =
                            document.getElementsByClassName('pdf-viewer');
                        for (var i = 0; i < pdfs.length; i++) {
                            const pdf = pdfs[i] as HTMLElement;
                            pdf.style.pointerEvents = 'auto';
                        }
                    }}
                >
                    <div
                        className={'handle ' + style.draggable}
                        style={{
                            zIndex: 60000,
                            position: 'absolute',
                            height: 'calc(100vh - 220px)',
                            cursor: 'col-resize !important'
                        }}
                        onMouseEnter={() => {
                            setHighlightDrag(true);
                        }}
                        onMouseLeave={() => {
                            setHighlightDrag(false);
                        }}
                        onDoubleClick={() => {
                            const ui = useStoreUI();
                            if (80 - contentSize < 1) {
                                draggableSpring.set(contentSize).start(50);
                            } else {
                                draggableSpring.set(contentSize).start(80);
                            }
                        }}
                    >
                        <img
                            src={dragIcon}
                            style={{
                                pointerEvents: 'none',
                                transform: 'scale(0.9)',
                                opacity: 0
                            }}
                        ></img>
                    </div>
                </Draggable>
            )}
            <div ref={ref} style={{ width: '100%', height: '100%' }}>
                <Flex direction="column" fillCell={true}>
                    <Split layoutPercent={size} spaceBetween={spaceBetween}>
                        {props.children[0]}
                        {
                            <div style={{ width: '100%', height: '100%' }}>
                                <img
                                    src={dragIcon}
                                    className={style.dragIconStyle}
                                    style={{
                                        filter: highlightDrag
                                            ? 'brightness(95%)'
                                            : 'none'
                                    }}
                                ></img>
                                {props.children[1]}
                            </div>
                        }
                    </Split>
                </Flex>
            </div>
        </>
    );
});
